// Shared progress modal for the streaming backup and the restore job. Polls
// GET /api/progress/{token} and renders a bar + counters. For restore, when the
// job pauses on a statement error (needsDecision), it shows Skip / Skip all /
// Abort buttons wired to props.onResolve.
//
// Props:
//   token      string                          — job token to poll
//   title      string
//   kind       'backup' | 'restore'
//   meta       [{label, value, mono?, muted?}] (optional) — compact info rows
//              rendered ABOVE the progress bar (used by the restore dialog to
//              show filename / from / into).
//   onResolve  (action) => Promise   (restore)  — "skip" | "skip-all" | "abort"
//   onRetry    () => Promise   (optional)        — re-run from scratch; a "↻ Retry"
//              button appears on a failed/aborted terminal state when provided.
//   onClose    fn()
import { createSignal, onMount, onCleanup, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import { getProgress } from '../lib/progress.js';
import { downloadImportReport, getImportReport } from '../lib/import.js';
import { formatBytes } from '../lib/format.js';

const TERMINAL = { done: 1, error: 1, aborted: 1 };

export default function ProgressModal(props) {
  const [snap, setSnap] = createSignal(null);
  const [resolving, setResolving] = createSignal(false);
  const [log, setLog] = createSignal(null);     // { headers, rows, total, shown } | null
  const [logBusy, setLogBusy] = createSignal(false);
  const [retrying, setRetrying] = createSignal(false);
  let timer;

  const retry = async () => {
    if (!props.onRetry) return;
    setRetrying(true);
    try { await props.onRetry(); }
    catch { /* parent toasts */ }
    finally { setRetrying(false); }
  };

  const toggleLog = async () => {
    if (log()) { setLog(null); return; } // collapse
    setLogBusy(true);
    try { setLog(await getImportReport(props.token)); }
    catch { /* ignore — button just no-ops */ }
    finally { setLogBusy(false); }
  };

  const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

  const poll = async () => {
    try {
      const s = await getProgress(props.token);
      if (s) {
        setSnap(s);
        if (TERMINAL[s.phase]) stop();
      }
    } catch {
      /* transient — keep polling */
    }
  };

  onMount(() => {
    poll();
    timer = setInterval(poll, 700);
    onCleanup(stop);
  });

  const phase = () => snap()?.phase || 'running';
  const pct = () => {
    const s = snap();
    if (!s || !s.total) return null; // indeterminate
    return Math.min(100, Math.round((s.current / s.total) * 100));
  };

  const resolve = async (action) => {
    setResolving(true);
    try {
      await props.onResolve?.(action);
      setSnap((s) => (s ? { ...s, needsDecision: false, phase: 'running' } : s));
      poll();
    } finally {
      setResolving(false);
    }
  };

  const itemNoun = () =>
    props.kind === 'restore' ? 'Statement' : props.kind === 'import' ? 'Row' : 'Table';

  return (
    <Modal open={true} title={props.title} onClose={props.onClose}>
      <div class="progress-modal">
        {/* Optional meta block — used by restore to surface filename / from / into
            before the user has any progress to look at. Rendered as a small
            two-column grid; falsy values are skipped so a missing origin shows
            as a clean "—" rather than an empty row. */}
        <Show when={Array.isArray(props.meta) && props.meta.some((m) => m && m.value != null && m.value !== '')}>
          <div class="progress-meta">
            <For each={props.meta.filter((m) => m && m.value != null && m.value !== '')}>
              {(m) => (
                <>
                  <span class="progress-meta-label">{m.label}</span>
                  <span class={`progress-meta-value${m.mono ? ' mono' : ''}${m.muted ? ' muted' : ''}`}>
                    {m.value}
                  </span>
                </>
              )}
            </For>
          </div>
        </Show>
        <Show when={snap()} fallback={<p class="muted">Starting…</p>}>
          {/* Bar */}
          <div class="progress-track">
            <div
              class={`progress-fill ${pct() === null ? 'indeterminate' : ''} phase-${phase()}`}
              style={pct() === null ? '' : `width:${pct()}%`}
            />
          </div>

          <div class="progress-stats">
            <span>
              {itemNoun()} {snap().current}{snap().total ? ` / ${snap().total}` : ''}
              <Show when={pct() !== null}><span class="muted"> · {pct()}%</span></Show>
            </span>
            <span class="muted">
              <Show when={props.kind === 'backup'}>
                {fmtNum(snap().rows)} rows · {formatBytes(snap().bytes)}
              </Show>
              <Show when={(props.kind === 'restore' || props.kind === 'import') && snap().skipped > 0}>
                {fmtNum(snap().skipped)} skipped
              </Show>
            </span>
          </div>

          <Show when={snap().label && !TERMINAL[phase()]}>
            <div class="progress-label mono">{snap().label}</div>
          </Show>

          {/* Paused on a statement error → ask the user */}
          <Show when={snap().needsDecision}>
            <div class="progress-error">
              <div class="progress-error-title">A statement failed:</div>
              <div class="mono progress-error-msg">{snap().error}</div>
              <div class="progress-decision">
                <button class="btn btn-sm" disabled={resolving()} onClick={() => resolve('skip')}>
                  Skip this one
                </button>
                <button class="btn btn-sm" disabled={resolving()} onClick={() => resolve('skip-all')}>
                  Skip all errors
                </button>
                <button class="btn btn-sm btn-danger" disabled={resolving()} onClick={() => resolve('abort')}>
                  Abort
                </button>
              </div>
            </div>
          </Show>

          {/* Terminal states */}
          <Show when={phase() === 'done'}>
            <div class="progress-final ok">✓ Complete{props.kind === 'backup' ? ' — download saved' : ''}.</div>
          </Show>
          <Show when={phase() === 'error'}>
            <div class="progress-final err">✗ Failed: <span class="mono">{snap().error}</span></div>
          </Show>
          <Show when={phase() === 'aborted'}>
            <div class="progress-final err">Aborted.<Show when={snap().error}> <span class="mono">{snap().error}</span></Show></div>
          </Show>
        </Show>

        <div class="progress-actions">
          {/* Retry (failed/aborted only) — re-runs the whole job from scratch. */}
          <Show when={props.onRetry && (phase() === 'error' || phase() === 'aborted')}>
            <span class="muted progress-retry-note">Retry re-runs the whole file from the start.</span>
            <button class="btn btn-sm" disabled={retrying()} onClick={retry}
                    title="Re-run the entire file from the beginning">
              {retrying() ? 'Retrying…' : '↻ Retry'}
            </button>
          </Show>
          {/* Import: view or download the rejected-rows report when any row was skipped. */}
          <Show when={props.kind === 'import' && (snap()?.reportRows || 0) > 0}>
            <button class="btn btn-sm btn-ghost" disabled={logBusy()} onClick={toggleLog}>
              {logBusy() ? 'Loading…' : log() ? 'Hide log' : `📋 Show log (${fmtNum(snap().reportRows)})`}
            </button>
            <button class="btn btn-sm btn-ghost" onClick={() => downloadImportReport(props.token)}>
              ⬇ Download CSV
            </button>
          </Show>
          <button class="btn btn-ghost" onClick={props.onClose}>
            {TERMINAL[phase()] ? 'Close' : 'Close (runs in background)'}
          </button>
        </div>

        {/* In-app rejected-rows log (toggle). */}
        <Show when={log()}>
          <div class="import-section-title" style="margin-top:10px;">
            Rejected rows <span class="muted">(showing {fmtNum(log().shown)} of {fmtNum(log().total)})</span>
          </div>
          <div class="import-preview-wrap" style="max-height:240px;overflow:auto;">
            <table class="import-preview">
              <thead><tr><th>Row</th><th>Reason</th><For each={log().headers}>{(h) => <th>{h}</th>}</For></tr></thead>
              <tbody>
                <For each={log().rows}>
                  {(r) => (
                    <tr>
                      <td class="mono">{r.row}</td>
                      <td class="import-reason">{r.reason}</td>
                      <For each={log().headers}>{(_h, i) => <td class="mono">{(r.values || [])[i()] ?? ''}</td>}</For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          <Show when={log().total > log().shown}>
            <div class="muted" style="font-size:12px;">…and {fmtNum(log().total - log().shown)} more (download the CSV for the full list).</div>
          </Show>
        </Show>
      </div>
    </Modal>
  );
}

function fmtNum(n) { return (n || 0).toLocaleString(); }
